import React, { Dispatch, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { accountApiUrl, appVirtualPath, login, setRedirectUrl } from "../../store/actions/account.actions";
import { HttpMethodType } from "../../store/models/HttpMethodType";
import { IAccount } from "../../store/models/IAccount";
import { IAccountState } from "../../store/models/IAccountState";
import { IAuthenticatedUser } from "../../store/models/IAuthenticatedUser";
import { IState } from "../../store/models/IState";
import { ApiHelper } from "../../utils/ApiHelper";
import LoadingIndicator from "../common/LoadingIndicator";
import MessageAlert, { MessageAlertType } from "../common/MessageAlert";

/**
 * Component to be displayed in the full page area to allow the user to click the login button.
 **/
const Login: React.FC = () => {
    const dispatch: Dispatch<any> = useDispatch();
    const accountState: IAccountState = useSelector((state: IState) => state.accountState);
    const account: IAccount = accountState?.account;
    const applicationState = useSelector((state: IState) => state.applicationState);
    const errorMessage: string = applicationState?.errorMessage;
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isGettingUserDetails, setIsGettingUserDetails] = useState(false);
    const redirectUrlQueryStringKey = "redirectUrl";
    // If not the root path then add a redirectUrl query string parameter to the login href that will be used as the redirect from the IDP after successful login.
    const redirect: string = (window.location.pathname !== "/") && (window.location.pathname !== appVirtualPath) ? `&${redirectUrlQueryStringKey}=${window.location.href}` : "";
    const loginHref = `${accountApiUrl}login?returnUrl=${window.location.href}${redirect}`;
    const title = "Welcome";

    function getRedirectUrl(): string {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl: string = urlParams.get(redirectUrlQueryStringKey) || "";
        return redirectUrl;
    }

    function getUserDetailsCallback(jsonResponse: any): void {
        setIsLoggingIn(false);
        const authenticatedUser: IAuthenticatedUser = jsonResponse as IAuthenticatedUser;

        const account: IAccount = {
            email: authenticatedUser.email,
            name: authenticatedUser.name,
            picture: authenticatedUser.picture,
            isAuthenticated: authenticatedUser.isAuthenticated,
            isAuthenticationEnabled: authenticatedUser.isAuthenticationEnabled
        }

        dispatch(login(account));

        if (authenticatedUser.isAuthenticated) {
            // Store the redirect in redux state to redirect after the account has successfully stored in redux state.
            dispatch(setRedirectUrl(getRedirectUrl()));

            // After a successful login try to redirect if required.
            const redirectUrl = getRedirectUrl();
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        }
    }
    
    /**
     * Call the API to get the authenticatedUser details and store in redux.
     */
    function getAuthenticatedUser() {
        if (!isGettingUserDetails && !account?.isAuthenticated) {
            setIsGettingUserDetails(true);
            setIsLoggingIn(true);
            ApiHelper.getApiResponse(`${accountApiUrl}getuserdetails`, HttpMethodType.Get, getUserDetailsCallback, dispatch);
        }
    }

    function getLoginContent(): JSX.Element | null {
        if (!account?.isAuthenticated) {
            getAuthenticatedUser();
        }

        if (!account?.isAuthenticated && !isLoggingIn) {
            return (
                <div className="text-center login-content">
                    <p>Log in to the Timesheet application.</p>
                    <Button variant="primary" href={loginHref} onClick={() => setIsLoggingIn(true)}>Login</Button>
                </div>
            );
        }

        return null;
    }

    return (
        <div id="content-wrapper">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-xl-10 col-lg-12 col-md-9">
                        <div className="card o-hidden border-0 shadow-lg my-5">
                            <div className="card-body p-0">
                                <div className="row">
                                    <div className="col-lg-6 d-none d-lg-block bg-login-image"></div>
                                    <div className="col-lg-6 login-right">
                                        <div className="p-5 login-container-right">
                                            <div className="brand d-flex align-items-center justify-content-center">
                                                <div className="account-icon login-icon">
                                                    <div className="sidebar-brand-icon" />
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <h1 className="h4 text-gray-900">{title}</h1>
                                            </div>
                                            <div className="login-container">
                                                {getLoginContent()}
                                                <LoadingIndicator isLoading={isLoggingIn} text="Logging in" />
                                                <MessageAlert message={errorMessage} type={MessageAlertType.Error} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
